import type { ActiveAlert, Asset, AssetState } from '~/types/asset'
import type { AlertLevelChangeMessage, AssetChangeData, AssetChangeMessage, AssetStatesMessage } from '~/types/socket'

const mergeLastStates = (asset: Asset): AssetState[] => {
  const parentStates = asset.lastStates || []
  const componentStates = Array.isArray(asset.components)
    ? asset.components.flatMap((component) => component.lastStates || [])
    : []
  return [...parentStates, ...componentStates]
}

const consolidateLastStates = (lastStates: AssetState[]): AssetState[] => {
  const items = new Map<string, AssetState>()

  for (const state of lastStates) {
    const previousState = items.get(state.stateKey)
    const stateDate = state.emissionDate || state.dateReceived
    const previousStateDate = previousState ? previousState.emissionDate || previousState.dateReceived : null
    if (!previousState || (previousStateDate && Date.parse(stateDate) >= Date.parse(previousStateDate))) {
      items.set(state.stateKey, state)
    }
  }

  return Array.from(items.values())
}

const LNLV_TYPES = ['lnlv', 'street_parking_management_panel_lnlv']

export const mapLastStates = (asset: Asset): AssetState[] => {
  // TODO: TEMP FOR MEP. MUST BE HANDLED IN THE BACKEND
  if (LNLV_TYPES.includes(asset.assetType?.id || '')) {
    return (asset.lastStates || []).filter((state) => state.stateKey.startsWith('LNLV_'))
  }

  const allStates = mergeLastStates(asset)
  return consolidateLastStates(allStates).filter((state) => !state.stateKey.startsWith('LNLV_'))
}

export const mapLastStatesForInventory = (asset: Asset): AssetState[] => {
  const allStates = mergeLastStates(asset)
  return consolidateLastStates(allStates)
}

const mergeActiveAlerts = (asset: Asset): ActiveAlert[] => {
  const parentAlerts = asset.activeAlerts || []
  const componentAlerts = Array.isArray(asset.components)
    ? asset.components.flatMap((component) => component.activeAlerts || [])
    : []
  return [...parentAlerts, ...componentAlerts].filter((item) => !item.isResolved)
}

const consolidateActiveAlerts = (alerts: ActiveAlert[]): ActiveAlert[] => {
  const items = new Map<string, ActiveAlert>()

  for (const alert of alerts) {
    const previousAlert = items.get(alert.alertKey)
    if (!previousAlert || Date.parse(alert.dateReceived) >= Date.parse(previousAlert.dateReceived)) {
      items.set(alert.alertKey, alert)
    }
  }

  return Array.from(items.values())
}

export const mapActiveAlerts = (asset: Asset): ActiveAlert[] => {
  const activeAlerts = mergeActiveAlerts(asset)
  return consolidateActiveAlerts(activeAlerts)
}

export const mapAssetStates = (message: AssetStatesMessage): AssetState[] => {
  return Object.entries(message.data.states).map(([key, value]) => ({
    id: 0,
    stateKey: key,
    stateValue: value,
    emissionDate: new Date(message.data.emissionDate * 1000).toISOString(),
    dateReceived: new Date(message.data.emissionDate * 1000).toISOString(),
    lastActionSendDate: '',
    lastActionSendId: null
  }))
}

export const mergeAssetStates = (existingStates: AssetState[] = [], newStates: AssetState[]): AssetState[] => {
  const stateMap = new Map<string, AssetState>()

  for (const state of existingStates) {
    stateMap.set(state.stateKey, state)
  }

  for (const state of newStates) {
    const existingState = stateMap.get(state.stateKey)
    const stateDate = state.emissionDate || state.dateReceived
    const existingStateDate = existingState ? existingState.emissionDate || existingState.dateReceived : null
    if (!existingState || (existingStateDate && Date.parse(stateDate) >= Date.parse(existingStateDate))) {
      stateMap.set(state.stateKey, state)
    }
  }

  return Array.from(stateMap.values())
}

export const isAlertLevelChangeForAsset = (asset: Asset | null, event: AlertLevelChangeMessage): boolean => {
  if (!asset) return false

  const targetAssetId = event.data.asset_id
  const isForOpenAsset = asset.id === targetAssetId
  const isForAComponent = asset.components?.some((component) => component.id === targetAssetId) ?? false

  return isForOpenAsset || isForAComponent
}

const getAssetChangeIds = ({ asset_id, headers, assets, removed_ids }: AssetChangeData): number[] => {
  const ids = [asset_id, ...(removed_ids ?? [])]
  const idIndex = headers?.indexOf('id') ?? -1

  if (headers && assets && idIndex !== -1) {
    for (let i = 0; i < assets.length; i += headers.length) {
      ids.push(Number(assets[i + idIndex]))
    }
  }

  return ids
}

export const isAssetChangeForAsset = (asset: Asset | null, event: AssetChangeMessage): boolean => {
  if (!asset) return false

  const openIds = new Set([asset.id, ...(asset.components ?? []).map((component) => component.id)])

  return getAssetChangeIds(event.data).some((id) => openIds.has(id))
}

export const deviceMessageMappers = {
  assets: mapAssetStates
  // TODO : alerts: mapDeviceAlerts
}
